/**
 * Module + page matching for navigation search.
 *
 * This is the shared engine behind every "find me a module or a page" surface:
 * the module picker (header switcher, sidebar dropdown, `ModulePickerSelect`)
 * and the global search bar / Cmd+K palette. Both must rank the same way — a
 * query that finds "Warehouses" in the picker has to find it in global search
 * too — so the scoring, synonym handling and cross-module rewriting live here
 * once rather than being reimplemented per surface.
 *
 * Matching itself comes from {@link searchScore} (multi-token, singular/plural
 * folding, small-typo tolerance); this module adds the navigation-specific
 * rules layered on top of it.
 */

import type { i18n as I18nInstance } from 'i18next';

import { TERMINOLOGY_DEFAULT_VARIABLES } from '../../i18n/terminologyDefaults';
import type { ModuleConfig, NavItem } from '../config/modules.config';
import { PARTIAL_MATCH_CEILING, STRONG_MATCH, searchScore } from './searchScore';

/** Resolves an i18n key to its display string (page label, section, description). */
export type TranslateKey = (key: string) => string;

/**
 * Resolves an i18n key to its ENGLISH string, whatever the UI language is, or
 * `''` when the key has no English translation.
 *
 * Every navigation-search surface takes one. English module and page names are
 * folded in as search aliases so a user working in Hebrew (or any other
 * language) can still type "integrations" and find the page — the product's
 * canonical vocabulary is English, it is what the docs, the URL and half the
 * team say, and a translated-only index makes those names unreachable.
 *
 * The parameter is REQUIRED rather than optional so a new surface cannot
 * silently ship without it — which is exactly how this regressed before.
 */
export type TranslateEnglishKey = TranslateKey;

/**
 * Build a {@link TranslateEnglishKey} from an i18next instance.
 *
 * The instance is injected rather than imported so a component resolves English
 * through the very instance it renders with (the one from `useTranslation`),
 * and tests can drive it with their own.
 *
 * Resolution goes through `t(key, { lng: 'en' })` — the same seam
 * `user_management/utils/permissionLabels` uses — rather than a bound
 * `getFixedT`, so it needs nothing of an instance beyond the two methods every
 * caller and test double already provides.
 *
 * The canonical ENGLISH terminology variables are passed explicitly, and that
 * is load-bearing rather than belt-and-braces. Every tenant-customizable term
 * is stored as an interpolation variable in EVERY locale file — the Contacts
 * page is literally `"{{Contacts}}"` in `en/common.json` and `he/common.json`
 * alike — and `{{Contacts}}` resolves through i18next's GLOBAL
 * `interpolation.defaultVariables`, which `applyTenantI18nOverrides` sets to the
 * tenant's terms for the ACTIVE locale. So `{ lng: 'en' }` alone returned the
 * Hebrew word: identical to the display name, folded away as a duplicate by
 * {@link englishAliases}, and the ~30 terminology-driven module names and page
 * labels (Contacts, Products, Deals, Tasks, Tickets, Agents, Employees,
 * Organizations, Contracts, …) were unreachable by their English names in a
 * non-English session — the exact gap the English alias exists to close.
 * Passing the map as interpolation DATA outranks the global defaults.
 */
export function createEnglishTranslator(
  instance: I18nInstance,
  ns: string = 'common',
): TranslateEnglishKey {
  return (key) =>
    instance.exists(key, { lng: 'en', ns })
      ? String(instance.t(key, { lng: 'en', ns, ...TERMINOLOGY_DEFAULT_VARIABLES }))
      : '';
}

/**
 * The English alias list for a display name: the English string, or nothing
 * when it is missing or already identical to what the user sees (so an
 * English-language session doesn't double-weight every name).
 *
 * Exported for surfaces that score their own rows rather than going through
 * {@link matchModules}/{@link matchModulePages} — the entity tier of the module
 * picker — so the dedupe rule is spelled once.
 */
export function englishAliases(english: string, display: string): string[] {
  if (!english) return [];
  return english.toLowerCase() === display.toLowerCase() ? [] : [english];
}

/** Resolves a module's display name (persona-aware — see `useModuleName`). */
export type ResolveModuleName = (module: ModuleConfig) => string;

/** A page (nav item) that matched the query, with the module that owns it. */
export interface ModulePageMatch {
  module: ModuleConfig;
  navItem: NavItem;
  /** Relevance score (higher first) — see {@link searchScore}. */
  score: number;
}

/** Modules that matched the query, plus every candidate's raw score. */
export interface ModuleMatchResult {
  /** Matching modules, most relevant first. */
  modules: ModuleConfig[];
  /** Score for every candidate module (0 = no match) — for regrouping/sorting. */
  scoreById: Map<string, number>;
}

/**
 * Pages are only searched once the query is at least this long — a single
 * character matches almost every page and would bury the module results.
 */
const MIN_PAGE_QUERY_LENGTH = 2;

/**
 * Relevance floor for a page: is this match good enough to show at all?
 *
 * A page is scored twice — against its own LABEL and against the wider haystack
 * (module name + section + label + description). Any positive haystack score
 * used to qualify, which let a page in on the weakest possible evidence: a
 * typo-tier hit (0.45–0.5) on a word it merely *mentions*. Searching a person's
 * name — "anat" — fuzzy-matched the word "Analytics" inside several modules'
 * page descriptions, so eight Reports/Tiles pages sat above the actual person.
 *
 * A page therefore qualifies on one of two grounds:
 *  - its haystack matched EVERY typed token at stem-or-better ({@link STRONG_MATCH}) —
 *    a real hit, wherever in the page's text it landed ("analytics" → Reports);
 *  - or its OWN LABEL matched at all, however weakly — a typo or partial on the
 *    page's name is still the page the user meant ("invoces" → Invoices,
 *    "freight services" → Freight Rates).
 *
 * What it excludes is exactly the noise band: fuzzy-or-partial evidence drawn
 * from something OTHER than the page's own name.
 */
function isDecentPageMatch(labelScore: number, haystackScore: number): boolean {
  return labelScore > 0 || haystackScore >= STRONG_MATCH;
}

/**
 * Match modules by name and search synonyms.
 *
 * Synonyms (e.g. "CRM" → pipeline) are language-independent search aliases
 * folded into searchScore's keyword haystack, so a module is findable by an
 * alternative name regardless of the UI language. The module's ENGLISH name is
 * folded in the same way (see {@link TranslateEnglishKey}), so "inventory"
 * finds the module in a Hebrew session.
 *
 * The English name comes from the module's literal `nameKey`, not from a
 * persona rename — so under the forwarder persona, Shipments is findable both
 * as its displayed "Freight Operations" and as "Shipments".
 *
 * An empty query matches every module (searchScore's "show everything"
 * contract) — callers that render a resting state handle that themselves.
 */
export function matchModules(
  modules: ModuleConfig[],
  query: string,
  getModuleName: ResolveModuleName,
  translateEnglish: TranslateEnglishKey,
): ModuleMatchResult {
  // Score every module once (reused for both filtering and sorting).
  const scoreById = new Map<string, number>();
  for (const module of modules) {
    const name = getModuleName(module);
    const aliases = [
      ...(module.searchSynonyms ?? []),
      ...englishAliases(translateEnglish(module.nameKey), name),
    ];
    scoreById.set(module.id, searchScore(name, query, aliases));
  }

  const scored = modules.filter((m) => (scoreById.get(m.id) ?? 0) > 0);
  // Exact takes preference over fuzzy: when any module matched at the
  // stem-or-better tier, drop the ones that only matched in the typo band — e.g.
  // "Contracts" for "contacts" must not sit above the exact "Contacts" page
  // (modules render above pages, so a surviving fuzzy module would outrank an
  // exact page). Fuzzy matches still surface as a fallback when nothing stronger
  // matched (genuine typos like "accounitng" → Accounting).
  const hasStrong = scored.some((m) => (scoreById.get(m.id) ?? 0) >= STRONG_MATCH);
  const filtered = hasStrong
    ? scored.filter((m) => (scoreById.get(m.id) ?? 0) >= STRONG_MATCH)
    : scored;

  const sorted = [...filtered].sort(
    (a, b) => (scoreById.get(b.id) ?? 0) - (scoreById.get(a.id) ?? 0),
  );

  return { modules: sorted, scoreById };
}

/**
 * Match pages (nav items) within modules, most relevant first.
 *
 * Each page is scored against a composed haystack — module name + section +
 * page label + description — so a mixture of module/submodule and page words
 * (e.g. "product categories", "master data categories") finds the page, and
 * singular/plural/typo tolerance comes for free from searchScore. Matches that
 * clear neither bar in {@link isDecentPageMatch} are dropped, so a page never
 * appears on a typo-tier hit against a word it merely mentions.
 *
 * Access is the CALLER's responsibility at both levels: it passes only the
 * modules the viewer may open, and an `isVisible` predicate for the nav items
 * within them (an admin-only page can live inside a module everyone may open).
 * The predicate is required rather than optional so a new surface cannot
 * silently list pages its viewer may not see — pass
 * {@link isNavItemVisible} bound to the viewer's access.
 *
 * @param modules - every candidate module (the same list passed to {@link matchModules})
 * @param matchedModuleIds - ids of the modules that have a row of their own
 *   above the page list. Their pages are ranked more conservatively, and the one
 *   page at `module.defaultPath` — the destination the module row itself opens —
 *   is dropped as a true duplicate of that row.
 * @param isVisible - per-nav-item access gate (see `utils/navItemAccess`)
 */
export function matchModulePages(
  modules: ModuleConfig[],
  query: string,
  matchedModuleIds: Set<string>,
  getModuleName: ResolveModuleName,
  translate: TranslateKey,
  translateEnglish: TranslateEnglishKey,
  isVisible: (navItem: NavItem) => boolean,
): ModulePageMatch[] {
  if (!query || query.length < MIN_PAGE_QUERY_LENGTH) return [];

  const matches: ModulePageMatch[] = [];

  for (const module of modules) {
    const moduleName = getModuleName(module);
    // English aliases for the module name and, below, each page label — so the
    // English vocabulary stays searchable in any UI language.
    const moduleNameAliases = englishAliases(translateEnglish(module.nameKey), moduleName);
    // When a module already has a row of its own above the page list, its pages
    // are ranked more conservatively: a page surfaces only when its OWN label is
    // a strong (stem-or-better) match, so "warehouses" — which hits Inventory
    // through its synonym — offers the Warehouses page without dragging every
    // other Inventory page along with it.
    //
    // A module that matched only PARTIALLY speaks for none of this: "agent
    // settings" partially matches the module "AI Agents" on one of its two
    // words, but the result the user is after is that module's Settings page.
    // The gate therefore ignores a partial match, exactly as it ignored the
    // zero score such a query used to produce.
    const moduleMatched =
      matchedModuleIds.has(module.id) &&
      searchScore(moduleName, query, [
        ...(module.searchSynonyms ?? []),
        ...moduleNameAliases,
      ]) >= PARTIAL_MATCH_CEILING;

    for (const navItem of module.navItems) {
      // Redundancy with the module row is a property of the DESTINATION, not of
      // why the module matched: clicking the module row navigates to
      // `module.defaultPath`, so the one page that repeats it is the page
      // sitting at that path. (17 of the 27 modules with nav items have such a
      // page; the other 10 — pipeline and tasks among them — reach their landing
      // page through no nav item at all, and for those this is a no-op and the
      // STRONG_MATCH label filter below does all the work.)
      //
      // The sidebar hides such an item for the same reason, but it honours an
      // `alwaysShowInNav` opt-out and this deliberately does not. That flag says
      // "keep this row in the sidebar's own list even though it is the landing
      // page" — a completeness choice about a permanent nav tree. A search
      // result list is not that: the module row is sitting immediately above,
      // and offering the same URL twice in one ranked list is noise, not
      // completeness. All four of its users today (people, ai_agents, shipments,
      // chatbot) carry it on their Dashboard, which is exactly the page the
      // module row already opens.
      //
      // This used to be inferred from the module having matched by NAME, which
      // dropped EVERY page of such a module. That is only ever right for modules
      // whose landing page shares the module's name, and this tree's are mostly
      // dashboards: searching "pipeline" hid `/pipeline/pipelines`, "shipments"
      // hid the Shipments list, "tasks" hid All Tasks, "users" hid the Users
      // page — each time leaving OTHER modules' weaker matches on screen as the
      // only page results.
      if (moduleMatched && navItem.path === module.defaultPath) continue;
      // Item-level gate (requiresAdmin / requiresSysadmin / connection /
      // persona). Applied before scoring so a page the viewer cannot open is
      // never ranked, counted or offered.
      if (!isVisible(navItem)) continue;
      const label = translate(navItem.labelKey);
      // navItem.searchSynonyms are language-independent aliases for the page
      // (e.g. "estimates" → Quotes), folded into the keyword haystack.
      // Findability is driven by the full haystack (so a page is still found
      // by words drawn from its module/section/description). Ranking, however,
      // is driven primarily by the page LABEL: a page whose own name matches
      // (e.g. "Contacts" for "contacts") must outrank one that merely mentions
      // the word in its description (e.g. Pipeline > Leads — "Contacts in the
      // early sales journey"). Without this they'd tie on the same
      // non-first-word tier (the module name is always haystack word 0) and
      // sort by nav order. The label score is weighted above any single-field
      // haystack score (max ~3), giving a clean label-primary, haystack-tiebreak
      // ordering.
      const labelAliases = [
        ...(navItem.searchSynonyms ?? []),
        ...englishAliases(translateEnglish(navItem.labelKey), label),
      ];
      const labelScore = searchScore(label, query, labelAliases);
      // A page within a module that already has its own row is surfaced only
      // when its OWN label is a strong (stem-or-better) match — so a direct hit
      // like "Warehouses" or "Pipelines" appears without flooding the results
      // with every other page of that module, each of which carries the module
      // name in its haystack.
      if (moduleMatched && labelScore < STRONG_MATCH) continue;
      // Deliberate scope cut: the haystack's section and description text is
      // the CURRENT language only — English aliases cover the module name and
      // the page label, the two things a user types when they mean "take me
      // there". Folding a whole English description in as an alias would widen
      // every page's keyword surface by a sentence, which is the shape that
      // produced the "anat" → eight Analytics pages noise above.
      const haystack = [
        moduleName,
        navItem.section ? translate(navItem.section) : '',
        label,
        navItem.descriptionKey ? translate(navItem.descriptionKey) : '',
      ]
        .filter(Boolean)
        .join(' ');
      const haystackScore = searchScore(haystack, query, [...labelAliases, ...moduleNameAliases]);
      if (isDecentPageMatch(labelScore, haystackScore)) {
        matches.push({ module, navItem, score: labelScore * 10 + haystackScore });
      }
    }
  }

  // Prefer the canonical master-data page over a cross-module shortcut.
  // Cross-module shortcut nav items (navItem.crossModule set) are secondary
  // pointers to a canonical page owned by another module — e.g. inventory's
  // "Products" and pipeline's "Contacts"/"Organizations" all point at
  // master_data. When such a shortcut matches, rewrite it to the canonical
  // page so searching "products"/"contacts" surfaces the master-data page
  // (the main location) rather than the module shortcut. This matters because
  // master_data matches the same queries by module synonym, which suppresses
  // its own pages — leaving the shortcut as the only page match; rewriting it
  // restores the canonical page. The canonical page shares the shortcut's
  // trailing path segment (.../products, .../contacts). If the canonical
  // module or page can't be resolved, the shortcut is left intact.
  //
  // Note this runs AFTER the per-module defaultPath suppression above, which
  // judged each shortcut against its OWN module. No shortcut in the config today
  // rewrites onto the canonical module's `defaultPath`; one that did would
  // reintroduce a row duplicating that module's own — the dedupe below catches
  // page-vs-page collisions only, not page-vs-module-row.
  const pathLeaf = (path: string) => path.split('/').filter(Boolean).pop() ?? path;
  const moduleById = new Map(modules.map((m) => [m.id, m]));
  const rewritten = matches.map((match) => {
    if (!match.navItem.crossModule) return match;
    const canonicalModule = moduleById.get(match.navItem.crossModule);
    // The canonical page is itself gated — never rewrite a shortcut the viewer
    // may follow into a canonical page they may not see.
    const canonicalNav = canonicalModule?.navItems.find(
      (n) => !n.crossModule && pathLeaf(n.path) === pathLeaf(match.navItem.path) && isVisible(n),
    );
    if (canonicalModule && canonicalNav) {
      return { module: canonicalModule, navItem: canonicalNav, score: match.score };
    }
    return match;
  });

  // Deduplicate by target page — a rewritten shortcut can now coincide with an
  // independently-matched canonical page (or another shortcut to the same
  // page). Keep the strongest score for each target.
  const byPath = new Map<string, ModulePageMatch>();
  for (const match of rewritten) {
    const key = `${match.module.id} ${match.navItem.path}`;
    const existing = byPath.get(key);
    if (!existing || match.score > existing.score) byPath.set(key, match);
  }

  // Sort by relevance: strongest matches first.
  return [...byPath.values()].sort((a, b) => b.score - a.score);
}
